import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { DonateStep, DONATE_SEEN } from "@/components/DonateStep";

function GoogleButton({ onPress, label }) {
  return (
    <button type="button" className="google-btn" data-testid="google-login-button" onClick={onPress}>
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
        <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
      </svg>
      {label}
    </button>
  );
}

export default function LoginPage() {
  const t = useT();
  const nav = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const next = params.get("next") || "/";
  const { signIn, signUp, google } = useAuth();
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "signin");
  const [donated, setDonated] = useState(() => { try { return !!sessionStorage.getItem(DONATE_SEEN); } catch { return true; } });
  const skipDonate = () => { try { sessionStorage.setItem(DONATE_SEEN, "1"); } catch { /* private mode */ } setDonated(true); };
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState(params.get("error") === "google" ? t("Google sign-in didn't finish. Try again.") : "");
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      if (mode === "signin") await signIn(form.email, form.password);
      else await signUp(form.email, form.password, form.name, agreed);
      nav(next, { replace: true });
    } catch (err) {
      setError(err.response ? t(apiError(err) || "Something went wrong. Try again.") : t("No connection. Check your signal and try again."));
    } finally { setBusy(false); }
  };

  return (
    <div className="screen login-screen" data-testid="login-screen">
      <header className="sites-header">
        <div><div className="header-title">{mode === "signin" ? t("Sign in") : t("Create account")}</div>
          <div className="header-sub">{t("Save your sites and drop history")}</div></div>
        <button type="button" className="header-btn" data-testid="login-close" aria-label={t("Close")}
          onClick={() => (window.history.state?.idx > 0 ? nav(-1) : nav("/"))}><X size={20} /></button>
      </header>
      <div className="screen-body">
        {mode === "signup" && !donated ? (<>
          <div className="seg" data-testid="auth-mode-toggle-donate">
            <button type="button" data-testid="auth-mode-signin-donate" onClick={() => setMode("signin")}>{t("Sign in")}</button>
            <button type="button" className="seg-on">{t("Create account")}</button>
          </div>
          <DonateStep onSkip={skipDonate} />
        </>) : (<>
        <GoogleButton onPress={() => google(next)} label={t("Continue with Google")} />
        <div className="divider"><span>{t("or")}</span></div>
        <div className="seg" data-testid="auth-mode-toggle">
          <button type="button" className={mode === "signin" ? "seg-on" : ""} data-testid="auth-mode-signin" onClick={() => setMode("signin")}>{t("Sign in")}</button>
          <button type="button" className={mode === "signup" ? "seg-on" : ""} data-testid="auth-mode-signup" onClick={() => setMode("signup")}>{t("Create account")}</button>
        </div>
        <form className="auth-form" onSubmit={submit} data-testid="auth-form">
          {mode === "signup" && (
            <label className="field"><span className="label">{t("Name")}</span>
              <input className="text-input" data-testid="auth-name-input" autoComplete="name" value={form.name} onChange={set("name")} /></label>
          )}
          <label className="field"><span className="label">{t("Email")}</span>
            <input className="text-input" data-testid="auth-email-input" type="email" inputMode="email" autoComplete="email" required value={form.email} onChange={set("email")} /></label>
          <label className="field"><span className="label">{t("Password")}</span>
            <input className="text-input" data-testid="auth-password-input" type="password" required minLength={mode === "signup" ? 8 : 1}
              autoComplete={mode === "signup" ? "new-password" : "current-password"} value={form.password} onChange={set("password")} /></label>
          {mode === "signup" && <p className="note">{t("At least 8 characters.")}</p>}
          {mode === "signup" && (
            <label className="agree-row">
              <input type="checkbox" data-testid="auth-terms-checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
              <span>{t("I agree to the")} <Link to="/terms" target="_blank" rel="noopener" data-testid="auth-terms-link">{t("Disclaimer & Terms")}</Link></span>
            </label>
          )}
          {error && <div className="scan-message tone-danger" data-testid="auth-error">{error}</div>}
          <button type="submit" className="primary-btn" data-testid="auth-submit-button" disabled={busy || (mode === "signup" && !agreed)}>
            {busy ? t("One moment…") : mode === "signin" ? t("Sign in") : t("Create account")}
          </button>
        </form>
        <p className="note center">{t("The calculators work without an account. Sign in only to save.")}</p>
        <p className="note center">{t("By continuing you agree to the")} <Link to="/terms" data-testid="login-terms-link">{t("Disclaimer & Terms")}</Link>.</p>
        </>)}
      </div>
    </div>
  );
}
